<style scoped>
.dialog-table td:first-child {
  width: 200px;
  font-weight: 700 !important;
}
</style>

<template #default="dialog">
  <v-card>
    <v-toolbar color="#1b2643" dark>
      {{ $t('newPackage.confirmTable.header') }}
    </v-toolbar>
    <v-card-text style="padding: 0; overflow-x: scroll; max-height: 500px">
      <v-expansion-panels accordion multiple :value="[0, 1, 2]">
        <v-expansion-panel>
          <v-expansion-panel-header>
            <span class="text-h6">
              {{ $t('newPackage.confirmTable.subHeader') }}
            </span>
          </v-expansion-panel-header>

          <v-expansion-panel-content>
            <div class="d-flex flex-column" style="gap: 20px">
              <table class="dialog-table">
                <tr>
                  <td>{{ $t('newPackage.confirmTable.packageCode') }}</td>
                  <td>{{ packageCode }}</td>
                </tr>

                <tr>
                  <td>{{ $t('newPackage.confirmTable.packageName') }}</td>
                  <td>{{ packageName }}</td>
                </tr>

                <tr>
                  <td>{{ $t('newPackage.confirmTable.viewingPeriod') }}</td>
                  <td>
                    {{ formattedDateTime(startViewingDate, startViewingTime) }}
                    ~ {{ formattedDateTime(endViewingDate, endViewingTime) }}
                  </td>
                </tr>
              </table>
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>
        <v-expansion-panel>
          <v-expansion-panel-header>
            <span class="text-h6">
              {{ $t('newPackage.confirmTable.campaigns.title') }}
              {{ `(${campaignsList.length})` }}
            </span>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <div class="d-flex flex-column" style="gap: 20px">
              <div
                v-for="(campaign, index) in campaignsList"
                :key="index"
                class="d-flex flex-column"
              >
                <p class="text-body-1 font-weight-bold">
                  <u>
                    {{ $t('newPackage.confirmTable.campaigns.subTitle') }}
                    {{ index + 1 }}
                  </u>
                </p>
                <table class="dialog-table">
                  <tr>
                    <td>
                      {{ $t('newPackage.confirmTable.campaigns.body[0]') }}
                    </td>
                    <td>{{ campaign.name }}</td>
                  </tr>

                  <tr>
                    <td>
                      {{ $t('newPackage.confirmTable.campaigns.body[1]') }}
                    </td>
                    <td>
                      {{
                        campaign.isDynamic
                          ? $t('newPackage.campaign.dynamicScenarioHint')
                          : getScenarioNameById(campaign.scenarioDocId)
                      }}
                    </td>
                  </tr>

                  <tr>
                    <td>
                      {{ $t('newPackage.confirmTable.campaigns.body[2]') }}
                    </td>
                    <td>
                      {{
                        campaign.limits === null
                          ? $t('newPackage.campaign.unlimited')
                          : campaign.limits
                      }}
                    </td>
                  </tr>

                  <tr>
                    <td>
                      {{ $t('newPackage.confirmTable.campaigns.body[3]') }}
                    </td>
                    <td>
                      {{
                        formattedDateTime(
                          campaign.isSameAsViewingPeriod
                            ? startViewingDate
                            : campaign.startInterviewDate,
                          campaign.isSameAsViewingPeriod
                            ? startViewingTime
                            : campaign.startInterviewTime
                        )
                      }}
                      ~
                      {{
                        formattedDateTime(
                          campaign.isSameAsViewingPeriod
                            ? endViewingDate
                            : campaign.endInterviewDate,
                          campaign.isSameAsViewingPeriod
                            ? endViewingTime
                            : campaign.endInterviewTime
                        )
                      }}
                    </td>
                  </tr>
                </table>
              </div>
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>

        <v-expansion-panel>
          <v-expansion-panel-header>
            <span class="text-h6">
              {{ $t('newPackage.confirmTable.campaigns.coupon.title') }}
              {{ `(${couponList.length})` }}
            </span>
          </v-expansion-panel-header>

          <v-expansion-panel-content>
            <v-data-table
              :headers="headers"
              :items="couponList"
              :items-per-page="10"
              :footer-props="{
                'items-per-page-options': [10, 100, -1],
              }"
              dense
            ></v-data-table>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-card-text>

    <v-alert
      v-if="createErrorMessage"
      type="error"
      class="mx-4 mt-4"
      dismissible
      @input="createErrorMessage = ''"
    >
      {{ createErrorMessage }}
    </v-alert>

    <v-card-actions class="justify-end pt-4 pb-4">
      <v-btn text @click="$emit('cancel')" :disabled="isLoading">
        <v-icon left>mdi-arrow-u-left-top</v-icon>
        {{ $t('newPackage.confirmTable.campaigns.cancel') }}
      </v-btn>
      <v-btn dark color="#1b2643" @click="createPackage()" :loading="isLoading">
        <v-icon left>mdi-check</v-icon>
        {{ $t('newPackage.confirmTable.campaigns.create') }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script lang="ts">
// Components
import { defineComponent, type PropType } from "@vue/composition-api";
// Services
import { ResponseStatus } from "@/api/apiClient";
import { campaignService } from "@/api/campaignService";
import { packageCouponService } from "@/api/packageCouponService";
import { packageService } from "@/api/packageService";
// Utilities
import { createIsoUtcDateTime } from "@/components/utils/dateUtils";
import type { AgentGroup } from "@/resources/agentGroup";
import type { EditableCoupon } from "@/resources/coupon";
import type { NewCampaign } from "@/resources/newCampaign";
import type { ScenarioWithId } from "@/resources/scenario";
import store from "@/store";
// Helpers
import { createPackageCampaignPayload } from "@/utils/campaignUtils";
import { getLocalizedErrorKey } from "@/utils/errorUtils";
import { fetchScenarios, getScenarioNameById } from "@/utils/scenarioUtils";

export default defineComponent({
  name: "ConfirmCreateCampaignTable",

  props: {
    campaigns: {
      type: Array as PropType<NewCampaign[]>,
      required: true,
      default: () => [
        {
          name: "",
          scenarioDocId: "",
          startInterviewDate: null,
          startInterviewTime: "",
          endInterviewDate: null,
          endInterviewTime: "",
          errorMessage: "",
        },
      ],
    },
    scenarios: {
      type: Array as PropType<ScenarioWithId[]>,
      required: true,
      default: () => [],
    },
    agentGroups: {
      type: Array as PropType<AgentGroup[]>,
      required: true,
      default: () => [],
    },
    coupons: {
      type: Array as PropType<EditableCoupon[]>,
      default: () => [
        {
          couponCode: "",
          password: "",
        },
      ],
    },
    packageCode: {
      type: String,
      default: "",
    },
    packageName: {
      type: String,
      default: "",
    },
    startViewingDate: {
      type: String as PropType<string | null>,
      default: "",
    },
    startViewingTime: {
      type: String,
      default: "",
    },
    endViewingDate: {
      type: String as PropType<string | null>,
      default: "",
    },
    endViewingTime: {
      type: String,
      default: "",
    },
  },

  data() {
    return {
      campaignsList: [] as NewCampaign[],
      scenarioList: [] as ScenarioWithId[],
      couponList: [] as EditableCoupon[],
      customerId: store.getters.customerId as string,
      availableScenarios: [] as ScenarioWithId[],

      headers: [
        {
          text: this.$t("newPackage.confirmTable.campaigns.coupon.body[0]"),
          value: "couponCode",
        },
        {
          text: this.$t("newPackage.confirmTable.campaigns.coupon.body[1]"),
          value: "password",
        },
      ],

      isLoading: false as boolean,
      createErrorMessage: "" as string,
    };
  },

  async created() {
    this.initialize();
  },

  methods: {
    async initialize() {
      this.campaignsList = this.$props.campaigns as NewCampaign[];
      this.scenarioList = this.$props.scenarios as ScenarioWithId[];
      this.couponList = this.coupons;
      this.availableScenarios = await fetchScenarios();
    },

    getScenarioNameById(scenarioId: string | undefined) {
      return getScenarioNameById(
        scenarioId,
        this.scenarioList,
        this.agentGroups,
      );
    },

    formattedDateTime(
      date: string | null | undefined,
      time: string | undefined,
    ) {
      if (!date) return time as string;
      if (!time) time = "00:00";
      const dateFormat = new Date(date);
      const year = dateFormat.getFullYear();
      const day = String(dateFormat.getDate()).padStart(2, "0");
      const month = String(dateFormat.getMonth() + 1).padStart(2, "0");

      const dateTime = year + "/" + month + "/" + day + " " + time;

      return dateTime as string;
    },

    serializeCampaignName(n: string): string {
      return n
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "");
    },

    async createPackage() {
      this.isLoading = true;
      this.createErrorMessage = "";

      const coupons = this.couponList.map((coupon) => {
        return {
          couponCode: coupon.couponCode.trim(),
          password: coupon.password.trim(),
          learnerId: null,
        };
      });

      try {
        const packageResponse = await packageService.create({
          code: this.packageCode.trim(),
          name: this.packageName,
          customerId: this.customerId,
          customerCode: store.getters.customerCode,
          campaignIds: [],
          openAt: createIsoUtcDateTime(
            this.startViewingDate,
            this.startViewingTime,
          ),
          couponCount: 0,
          closedAt: createIsoUtcDateTime(
            this.endViewingDate,
            this.endViewingTime,
          ),
        });
        if (
          packageResponse.status !== ResponseStatus.Ok ||
          !packageResponse.result
        ) {
          if (packageResponse.status === ResponseStatus.Conflict) {
            this.createErrorMessage = "";
            this.$emit("duplicateCode");
          } else if (packageResponse.status === ResponseStatus.BadRequest) {
            const errorKey = getLocalizedErrorKey(
              packageResponse.errorMessage,
              "newPackage.error.createPackageFailed",
            );
            this.createErrorMessage = this.$t(errorKey) as string;
          } else {
            this.createErrorMessage = this.$t(
              "newPackage.error.createPackageFailed",
            ) as string;
          }
          return;
        }
        const createdPackage = packageResponse.result;

        const campaignPayloads = this.campaignsList.map(
          (campaign: NewCampaign) => {
            const scenario = this.availableScenarios.find(
              (s) => s.id === campaign.scenarioDocId,
            );
            const startDate = campaign.isSameAsViewingPeriod
              ? this.startViewingDate
              : (campaign.startInterviewDate ?? null);
            const startTime = campaign.isSameAsViewingPeriod
              ? this.startViewingTime
              : (campaign.startInterviewTime ?? "");
            const endDate = campaign.isSameAsViewingPeriod
              ? this.endViewingDate
              : (campaign.endInterviewDate ?? null);
            const endTime = campaign.isSameAsViewingPeriod
              ? this.endViewingTime
              : (campaign.endInterviewTime ?? "");
            const isUnlimited = campaign.limits === null;
            return {
              ...createPackageCampaignPayload({
                packageId: createdPackage.id,
                campaignCode: crypto.randomUUID(),
                name: campaign.name,
                scenarioDocId: campaign.scenarioDocId,
                agentGroupId: scenario?.agentGroupId ?? "",
                isDynamic: campaign.isDynamic,
                customerCode: createdPackage.customerCode,
                customerId: createdPackage.customerId,
                openAt: createIsoUtcDateTime(
                  this.startViewingDate,
                  this.startViewingTime,
                ),
                closedAt: createIsoUtcDateTime(
                  this.endViewingDate,
                  this.endViewingTime,
                ),
                startAt: createIsoUtcDateTime(startDate, startTime),
                endAt: createIsoUtcDateTime(endDate, endTime),
              }),
              isUnlimited,
              limits: isUnlimited ? 0 : (campaign.limits ?? 1),
            };
          },
        );

        const createdCampaigns = await campaignService.createManyForPackage(
          createdPackage.id,
          campaignPayloads,
        );
        if (createdCampaigns.length < campaignPayloads.length) {
          this.$emit("resetValues");
          this.$emit("cancel");
          this.$router.push({
            name: "Home",
            params: {
              snackbarAction: "true",
              snackbarText: this.$t(
                "newPackage.error.partialFailure.campaignsFailed",
              ) as string,
              snackbarColor: "error",
              snackbarActionButtonText: this.$t("common.retry") as string,
              snackbarActionButtonRoute: `/dashboard/package/edit/${createdPackage.id}?couponError=true`,
            },
          });
          return;
        }

        const createdPackageCoupons = await packageCouponService.createMany(
          createdPackage.id,
          coupons,
        );
        if (
          coupons.length > 0 &&
          createdPackageCoupons.length < coupons.length
        ) {
          this.$emit("resetValues");
          this.$emit("cancel");
          this.$router.push({
            name: "Home",
            params: {
              snackbarAction: "true",
              snackbarText: this.$t(
                "newPackage.error.partialFailure.couponsFailed",
              ) as string,
              snackbarColor: "error",
              snackbarActionButtonText: this.$t("common.retry") as string,
              snackbarActionButtonRoute: `/dashboard/package/edit/${createdPackage.id}?couponError=true`,
            },
          });
          return;
        }

        this.$emit("resetValues");
        this.$emit("cancel");
        this.$router.push({
          name: "Home",
          params: {
            snackbarAction: "true",
            snackbarText: this.$t(
              "newPackage.success.packageCreated",
            ) as string,
            snackbarColor: "blue",
            snackbarActionButtonText: this.$t(
              "newPackage.success.viewPackage",
            ) as string,
            snackbarScrollToSelector: `[data-package-id="${createdPackage.id}"]`,
          },
        });
      } catch (error) {
        console.error("error:", error);
        this.createErrorMessage = this.$t(
          "newPackage.error.createPackageFailed",
        ) as string;
      } finally {
        this.isLoading = false;
      }
    },
  },
});
</script>
